import { useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { StyledText } from "styles/Text";
import { colors } from "constants/colors";
import { HeaderMainTitleComponent } from "components/molecules/Header";
import {
  useGetBenefitCategoryProducts,
  useGetBenefitMain,
  usePostBenefitViewLog,
} from "api/queries";
import HeaderTab from "components/molecules/HeaderTab";
import ImageButton from "components/molecules/ImageButton";
import DropdownPicker from "components/molecules/DropdownPicker";
import { DropdownOption } from "components/molecules/Dropdown";
import { useErrorStore } from "stores/errorStore";
import { useAuthStore } from "stores/authStore";
import { ProductListModel } from "models/productListModel";
import { isAndroid } from "react-device-detect";
import { RadioType } from "types/Common";
import ProductList from "components/organisms/ProductList";
import BenefitMainProductList from "components/organisms/BenefitMainProductList";
import BenefitSectionTitle from "components/organisms/BenefitSectionTitle";
import Badge from "components/atoms/Badge";
import InfoMark from "components/atoms/InfoMark";
import { ApiPostBenefitViewLog } from "types/Benefit";
import RadioModal from "components/molecules/RadioModal";
import { API_IMAGE_CONTEXT } from "constants/api";
import { BenefitListModel } from "models/benefitListModel";
import { logEvent } from "firebase/analytics";
import Loading from "components/atoms/Loading";
import { analytics } from "lib/firebase";
import { usePageView } from "hooks/useFirebase";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Benefit = () => {
  usePageView("Benefit/main");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isTopButtonShow, setIsTopButtonShow] = useState(false);
  const [isContentShow, setIsContentShow] = useState(false); // 깜박이는 현상으로 탭 로드가 끝나면 컨텐츠 그려지게
  const [isShowRadioModal, setIsShowRadioModal] = useState(false);
  const [mainProducts, setMainProducts] = useState<ProductListModel[]>([]);
  const [goldProducts, setGoldProducts] = useState<ProductListModel[]>([]);
  const [personalProducts, setPersonalProducts] = useState<ProductListModel[]>(
    [],
  );
  const [frontBenefitList, setFrontBenefitList] = useState<BenefitListModel[]>(
    [],
  );
  const [orderTy, setOrderTy] = useState<number | string>(10);

  const [tabList, setTabList] = useState<RadioType[] | undefined>(undefined);
  const [isSubLoaded, setIsSubLoaded] = useState(false);
  const deviceId = useAuthStore((state) => state.deviceId);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [tabScrollToIndex, setTabScrollToIndex] = useState<number>(0);
  const osType = isAndroid ? "1" : "2";
  const token = useAuthStore((state) => state.token) || "";
  const timezone = useAuthStore((state) => state.timezone) || "";
  const appVersion = useAuthStore((state) => state.appVersion) || "";
  const codepushVersion = useAuthStore((state) => state.codepushVersion) || "";

  const {
    data: mainData,
    refetch: mainDataRefetch,
    isLoading: isMainDataLoading,
  } = useGetBenefitMain({
    token,
    deviceId,
    osType,
  });
  const {
    data: categoryProductsData,
    refetch: categoryProductsRefetch,
    hasNextPage,
    fetchNextPage,
    isSuccess,
  } = useGetBenefitCategoryProducts({
    categoryCd: activeTab,
    orderTy,
    enabled: !!activeTab,
  });
  const viewLogMutation = usePostBenefitViewLog();

  const { setError } = useErrorStore();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > 10) {
        setIsTopButtonShow(true);
      } else {
        setIsTopButtonShow(false);
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!mainData) {
      return;
    }
    mainData.mainProductList && setMainProducts(mainData.mainProductList);
    mainData.goldBoxList && setGoldProducts(mainData.goldBoxList);
    mainData.personalList && setPersonalProducts(mainData.personalList);

    const tabList = [
      { id: 0, label: "홈" },
      ...(mainData.categoryList ?? []).map((item) => ({
        id: item.categoryCd ?? 0,
        label: item.categoryNm ?? "",
      })),
    ];

    setTabList(tabList);

    if (mainData.benefitList) {
      const tempBenefitList = [...mainData.benefitList].sort((a, b) => {
        return (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0);
      });
      setFrontBenefitList(tempBenefitList);
    }
  }, [mainData]);

  const categoryProducts = useMemo(() => {
    const categoryItems = categoryProductsData?.pages.reduce(
      (acc, val) => acc.concat(val.items),
      [] as ProductListModel[],
    );
    return categoryItems || [];
  }, [categoryProductsData]);

  const categoryProductsTotalCount = useMemo(() => {
    return categoryProductsData?.pages[0].totalCount || 0;
  }, [categoryProductsData]);

  useEffect(() => {
    setOrderTy(10);
  }, [activeTab]);

  useEffect(() => {
    if (isSuccess) {
      setIsSubLoaded(isSuccess);
    } else {
      isSubLoaded && setIsSubLoaded(false);
    }
  }, [isSuccess]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    logEvent(analytics, "Web_benefit_tap", { id: index });
    scrollTop();
  };

  const handleRadioModalItemClick = (id: number, index: number) => {
    setIsShowRadioModal(false);
    handleTabClick(id);
    setTabScrollToIndex(index);
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const onPressBenefitItem = (
    benefitId: number,
    type?: string,
    url?: string,
  ) => {
    if (window.ReactNativeWebView) {
      const params: ApiPostBenefitViewLog = {
        benefitId,
        osType,
        buttonType: "LB",
      };
      viewLogMutation.mutate(params);

      // BPT010 : 내부링크형
      // BPT020 : 외부링크형
      // BPT030 : 콘텐츠형
      if (type === "BPT010" && url) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "link", link: url }),
        );
      } else if (type === "BPT020" && url) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "openExternalUrl", link: url }),
        );
      } else if (type === "BPT030" && benefitId) {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: "appLink",
              navigateType: "push",
              link: "BenefitDetail",
              linkParam: { benefitId },
            }),
          );
        }
      }
    } else {
      setError({ errorMessage: "모바일이 아닙니다" });
    }
  };

  const options: DropdownOption[] = [
    { value: 10, label: "추천순" },
    { value: 20, label: "가격 낮은 순" },
    { value: 30, label: "가격 높은 순" },
  ];

  const onGoldboxTimerReached = () => {
    mainDataRefetch();
  };

  const onCategoryTimeReached = () => {
    categoryProductsRefetch();
  };

  const mainTitleOnPress = (index: number) => {
    setActiveTab(index);

    const scrollTabIndex = tabList?.findIndex((item) => item.id === index);
    setTabScrollToIndex(scrollTabIndex ?? 0);
  };

  const onTnkFactory = (isBanner?: boolean) => {
    logEvent(
      analytics,
      isBanner ? "Web_benefit_home_plus_banner" : "Web_benefit_top_plus_icon",
    );

    try {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: "link",
          link: `https://webfeed.tnkfactory.com/tnk/cpp.weboff.main?app_id=10e090f0-2051-89ea-ae98-14060e09060a&web_yn=Y`,
        }),
      );
    } catch (e) {
      console.log("offerwall error", e);
    }
  };

  // 제휴처 카테고리는 1100부터
  const isShowFooter =
    activeTab < 1100 && (activeTab === 0 || categoryProducts.length > 0);

  const onHeadrTabLayout = () => {
    setIsContentShow(true);
  };

  if (isMainDataLoading) {
    return <Loading />;
  }

  return (
    <StyledContainer>
      <h1 className="hide">열달후에</h1>
      <StyledHeader>
        <HeaderMainTitleComponent>
          <StyledHeaderInner>
            {isContentShow && (
              <>
                <StyledText.Headline17 as="h2">혜택</StyledText.Headline17>
                <ImageButton
                  width={36}
                  height={36}
                  source={require("assets/images/store/ic_point_normal_outlined_black_m.png")}
                  onClick={onTnkFactory}
                />
              </>
            )}
          </StyledHeaderInner>
        </HeaderMainTitleComponent>
        {tabList && (
          <HeaderTab
            tabList={tabList}
            onClick={handleTabClick}
            activeTabId={activeTab}
            isButton
            onButtonClick={() => setIsShowRadioModal(true)}
            scrollToIndex={tabScrollToIndex}
            onLoad={onHeadrTabLayout}
          />
        )}
      </StyledHeader>

      {isContentShow && (
        <>
          <StyledButtonBox>
            <StyledBannerImage
              src={require("assets/images/store/banner_type_a_984x228_kakao.png")}
              onClick={() => onTnkFactory(true)}
              alt="열달에서 역대급 혜택 쌓기 시작! 카카오페이포인트를 빨리 적립 받는 방법"
            />
          </StyledButtonBox>
          {activeTab === 0 && frontBenefitList.length > 0 && (
            <StyledBenefitZone>
              <BenefitSectionTitle
                title="열달 혜택존"
                text="지금 이용 가능한 혜택! 놓치면 아까워요"
              />
              <StyledBenefitList>
                {frontBenefitList.map((item, index) => {
                  const src = `${API_IMAGE_CONTEXT}${item.thumbnailImgPath}`;
                  return (
                    <StyledBenefitItem
                      key={`benefit_${item.benefitId}_${index}`}
                      onClick={() =>
                        item.benefitId &&
                        onPressBenefitItem(
                          item.benefitId,
                          item.typeCd,
                          item.url,
                        )
                      }
                    >
                      <StyledBenefitImage src={src} alt="" />
                      <StyledBenefitTextBox>
                        {!!item.isClosingSoon && (
                          <StyledEndBox>
                            <InfoMark />
                            <StyledText.Sub11 color={colors.mainOrange} as="p">
                              마감임박
                            </StyledText.Sub11>
                          </StyledEndBox>
                        )}
                        <StyledText.Headline15
                          as="em"
                          color={colors.subBody3}
                          style={{
                            overflow: "hidden",
                            width: "100%",
                            textWrap: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.mainTitle}
                        </StyledText.Headline15>
                        <StyledText.Sub12
                          as="p"
                          color={colors.darkGray1}
                          style={{
                            overflow: "hidden",
                            width: "100%",
                            textWrap: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.subTitle}
                        </StyledText.Sub12>
                      </StyledBenefitTextBox>
                      <Badge
                        bgColor={colors.subPurple4}
                        textComponent={
                          <StyledText.Headline12 color={colors.white}>
                            받기
                          </StyledText.Headline12>
                        }
                      />
                    </StyledBenefitItem>
                  );
                })}
              </StyledBenefitList>
            </StyledBenefitZone>
          )}

          <StyledSection>
            {activeTab !== 0 &&
              categoryProducts.length > 0 &&
              !!categoryProductsTotalCount && (
                <StyledDropdownBox ref={dropdownRef}>
                  <StyledText.Body13 color={colors.darkGray1}>
                    총{" "}
                    <StyledText.Body13>
                      {categoryProductsTotalCount}
                    </StyledText.Body13>
                    개
                  </StyledText.Body13>
                  <DropdownPicker
                    options={options}
                    selectedValue={Number(orderTy)}
                    onChangeValue={setOrderTy}
                  />
                </StyledDropdownBox>
              )}

            <StyledProductSection>
              {activeTab === 0 ? (
                <BenefitMainProductList
                  mainProductsInfo={{
                    productData: mainProducts,
                    title: mainData?.mainProductTitle || "",
                    subTitle: mainData?.mainProductSubTitle || "",
                  }}
                  goldProductsInfo={{
                    productData: goldProducts,
                    title: mainData?.goldBoxTitle || "",
                    subTitle: mainData?.goldBoxSubTitle || "",
                    onTimerReached: onGoldboxTimerReached,
                  }}
                  personalProductsInfo={{
                    productData: personalProducts,
                    title: mainData?.personalTitle || "",
                    subTitle: mainData?.personalSubTitle || "",
                  }}
                  setActiveTab={mainTitleOnPress}
                  partnerProductList={mainData?.partnerProductList}
                />
              ) : (
                <StyledProductContainer>
                  <ProductList
                    productData={categoryProducts}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isTimer={activeTab === 1099}
                    onTimerReached={onCategoryTimeReached}
                    isLoaded={isSubLoaded}
                  />
                </StyledProductContainer>
              )}
            </StyledProductSection>
          </StyledSection>

          {isShowFooter && (
            <StyledAttention>
              <StyledText.Headline12 color={colors.darkGray1} as="strong">
                안내사항
              </StyledText.Headline12>
              <StyledText.Headline12 color={colors.gray} as="p">
                * 소비자가 옆에 표시된 등락율은 직전 가격과 비교하여 표시됩니다.
              </StyledText.Headline12>
              <StyledText.Sub12 color={colors.gray} as="p">
                * 추천 순은 직전 가격대비 떨어진 금액 차이가 많이나는 순서대로
                정렬됩니다.
              </StyledText.Sub12>
              <StyledText.Sub12 color={colors.gray} as="p">
                * 혜택 페이지에 표기된 쿠팡 상품 및 가격 정보는 실제 내용과 다를
                수 있습니다. 구매 시 쿠팡의 실제 정보를 꼭 확인해주세요.
              </StyledText.Sub12>
              <StyledText.Sub12 color={colors.gray} as="p">
                * 실제 상품의 내용을 확인하지 않고 구매하여 발생한 손실 및
                손해에 대한 책임은 이용자 본인에게 있습니다.
              </StyledText.Sub12>
              <StyledText.Sub12 color={colors.gray} as="p">
                * 해당 페이지의 쿠팡으로 이동하는 링크는 쿠팡 파트너스 활동의
                일환으로, 이에 따른 일정액의 수수료를 제공받습니다.
              </StyledText.Sub12>
            </StyledAttention>
          )}

          <StyledTopButton $isShow={!!isTopButtonShow}>
            <ImageButton
              width={36}
              height={36}
              source={require("assets/images/ic_arrowtop_normal_outlined_black_m.png")}
              onClick={scrollTop}
            />
          </StyledTopButton>
        </>
      )}

      <RadioModal
        isVisible={isShowRadioModal}
        selectedId={activeTab}
        list={tabList ?? []}
        onChange={(id, index) => handleRadioModalItemClick(id, index ?? 0)}
        titleMessage="카테고리"
        closeModal={() => setIsShowRadioModal(false)}
      />
    </StyledContainer>
  );
};

export default Benefit;

const StyledContainer = styled.section``;

const StyledBenefitZone = styled.div`
  padding-top: 24px;
`;

const StyledSection = styled.section`
  min-height: 100vh; // 화면 그릴때 푸터 배경이 먼저 깜빡이듯 보여서 추가
  margin: 0px auto;
`;

const StyledProductSection = styled.section`
  margin-bottom: 40px;
`;

const StyledProductContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled.div`
  z-index: 4;
  position: sticky;
  top: 0;
  background-color: ${colors.white};
`;

const StyledHeaderInner = styled.div`
  display: flex;
  flex: 1;
  padding-right: 16px;
  align-items: center;
  justify-content: space-between;
`;

const StyledDropdownBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 16px 0px 20px;
`;

const StyledBenefitList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0 16px 40px;
  gap: 12px;
  border-bottom: 12px solid ${colors.dimBg};
`;

const StyledBenefitItem = styled.li`
  display: flex;
  flex-direction: row;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${colors.lightGray1};
  background-color: ${colors.white};
  align-items: center;
`;

const StyledBenefitImage = styled.img`
  width: 32px;
  height: 32px;
`;

const StyledBenefitTextBox = styled.div`
  overflow: hidden;
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: 12px;
  margin-right: 8px;
`;

const StyledEndBox = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

const StyledTopButton = styled.div<{ $isShow: boolean }>`
  z-index: 5;
  position: fixed;
  bottom: 40px;
  right: 16px;
  width: 36px;
  height: 36px;
  padding: 4px;
  border: 1px solid ${colors.lightGray1};
  background-color: ${colors.white};
  border-radius: 22px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.24);
  opacity: ${({ $isShow }) => ($isShow ? 1 : 0)};
  transform: ${({ $isShow }) =>
    $isShow ? "translateY(0)" : "translateY(20px)"};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  ${({ $isShow }) =>
    $isShow &&
    css`
      animation: ${fadeIn} 0.3s ease-out;
    `}
`;

const StyledAttention = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 20px 80px;
  background-color: ${colors.dimBg};
`;

const StyledButtonBox = styled.div`
  margin-top: 24px;
  padding: 0px 20px;
`;

const StyledBannerImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 12px;
`;
